import React from 'react';
import ShareRewindSlideBase from './ShareRewindSlideBase';
import { RewindStats } from '../../utils/rewindStats';

interface ShareRewindShipHighlightsSlideProps {
  year: number;
  stats: RewindStats;
}

const ShareRewindShipHighlightsSlide = React.forwardRef<HTMLDivElement, ShareRewindShipHighlightsSlideProps>(
  ({ year, stats }, ref) => {
    if (!stats.shipHighlights || Object.keys(stats.shipHighlights).length === 0) {
      return null;
    }

    const { largest, smallest, oldest, newest } = stats.shipHighlights;
    const highlightCount = Object.keys(stats.shipHighlights).length;
    
    // Scale images and text based on number of highlights (1-4)
    // More dramatic scaling for images - make them much bigger
    let imageHeight = 'h-72';
    let labelSize = 'text-4xl';
    let shipNameSize = 'text-5xl';
    let detailSize = 'text-3xl';
    let cardPadding = 'p-8';
    
    if (highlightCount === 1) {
      imageHeight = 'h-[600px]'; // Very large for single highlight - almost full height
      labelSize = 'text-6xl';
      shipNameSize = 'text-7xl';
      detailSize = 'text-5xl';
      cardPadding = 'p-12';
    } else if (highlightCount === 2) {
      imageHeight = 'h-96'; // Large for 2 highlights
      labelSize = 'text-5xl';
      shipNameSize = 'text-6xl';
      detailSize = 'text-4xl';
      cardPadding = 'p-10';
    } else if (highlightCount === 3) {
      imageHeight = 'h-80'; // Medium-large for 3 highlights
      labelSize = 'text-4xl';
      shipNameSize = 'text-5xl';
      detailSize = 'text-3xl';
      cardPadding = 'p-8';
    } else {
      // 4 highlights - make images bigger than before
      imageHeight = 'h-72'; // Increased from h-56 to h-72
      labelSize = 'text-4xl';
      shipNameSize = 'text-5xl';
      detailSize = 'text-3xl';
      cardPadding = 'p-8';
    }

    return (
      <ShareRewindSlideBase ref={ref} year={year}>
        <div className="flex flex-col h-full">
          {/* Top Section - Title */}
          <div className="flex flex-col items-center justify-center text-center pt-20 pb-8">
            <div className="text-7xl font-bold text-white leading-none mb-4">
              Ship Highlights
            </div>
          </div>

          {/* Middle Section - Highlights Grid */}
          <div className="flex-1 flex items-center justify-center px-8">
            <div className="grid grid-cols-2 gap-8 w-full max-w-5xl">
              {largest && (
                <div className={`bg-white/10 backdrop-blur-sm rounded-lg ${cardPadding} text-center flex flex-col`}>
                  {largest.imageUrl && (
                    <img
                      src={largest.imageUrl}
                      alt={largest.shipName}
                      className={`w-full ${imageHeight} object-cover rounded-lg mb-4`}
                      crossOrigin="anonymous"
                    />
                  )}
                  <div className={`${labelSize} font-bold text-white mb-2`}>Largest Ship</div>
                  <div className={`${shipNameSize} font-bold text-white mb-2`}>{largest.shipName}</div>
                  <div className={`${detailSize} text-white/90`}>{largest.cruiseLine}</div>
                  <div className={`${detailSize} text-white/80 mt-2`}>{largest.tonnage} GRT</div>
                </div>
              )}

              {smallest && (
                <div className={`bg-white/10 backdrop-blur-sm rounded-lg ${cardPadding} text-center flex flex-col`}>
                  {smallest.imageUrl && (
                    <img
                      src={smallest.imageUrl}
                      alt={smallest.shipName}
                      className={`w-full ${imageHeight} object-cover rounded-lg mb-4`}
                      crossOrigin="anonymous"
                    />
                  )}
                  <div className={`${labelSize} font-bold text-white mb-2`}>Smallest Ship</div>
                  <div className={`${shipNameSize} font-bold text-white mb-2`}>{smallest.shipName}</div>
                  <div className={`${detailSize} text-white/90`}>{smallest.cruiseLine}</div>
                  <div className={`${detailSize} text-white/80 mt-2`}>{smallest.tonnage} GRT</div>
                </div>
              )}

              {oldest && (
                <div className={`bg-white/10 backdrop-blur-sm rounded-lg ${cardPadding} text-center flex flex-col`}>
                  {oldest.imageUrl && (
                    <img
                      src={oldest.imageUrl}
                      alt={oldest.shipName}
                      className={`w-full ${imageHeight} object-cover rounded-lg mb-4`}
                      crossOrigin="anonymous"
                    />
                  )}
                  <div className={`${labelSize} font-bold text-white mb-2`}>Oldest Ship</div>
                  <div className={`${shipNameSize} font-bold text-white mb-2`}>{oldest.shipName}</div>
                  <div className={`${detailSize} text-white/90`}>{oldest.cruiseLine}</div>
                  <div className={`${detailSize} text-white/80 mt-2`}>Built {oldest.yearBuilt}</div>
                </div>
              )}

              {newest && (
                <div className={`bg-white/10 backdrop-blur-sm rounded-lg ${cardPadding} text-center flex flex-col`}>
                  {newest.imageUrl && (
                    <img
                      src={newest.imageUrl}
                      alt={newest.shipName}
                      className={`w-full ${imageHeight} object-cover rounded-lg mb-4`}
                      crossOrigin="anonymous"
                    />
                  )}
                  <div className={`${labelSize} font-bold text-white mb-2`}>Newest Ship</div>
                  <div className={`${shipNameSize} font-bold text-white mb-2`}>{newest.shipName}</div>
                  <div className={`${detailSize} text-white/90`}>{newest.cruiseLine}</div>
                  <div className={`${detailSize} text-white/80 mt-2`}>Built {newest.yearBuilt}</div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Section - Branding */}
          <div className="flex flex-col items-center text-center pb-12">
            <div className="text-4xl font-bold text-white leading-none mb-2">
              Cruising Along Rewind {year}
            </div>
          </div>
        </div>
      </ShareRewindSlideBase>
    );
  }
);

ShareRewindShipHighlightsSlide.displayName = 'ShareRewindShipHighlightsSlide';

export default ShareRewindShipHighlightsSlide;

